import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
const displayCount = (count) => (count > 9 ? '9+' : count);

const CategoryIcon = ({ category }) => {
  const common = { width: 21, height: 21, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.55, strokeLinecap: 'round', strokeLinejoin: 'round', focusable: 'false' };
  if (category === 'messages') {
    return <svg {...common}><path d="M20 11.5a7.5 7.5 0 0 1-8 7.5 8.6 8.6 0 0 1-3.5-.75L4 19l.9-3.2A7.2 7.2 0 0 1 4 12a7.5 7.5 0 0 1 8-7.5 7.5 7.5 0 0 1 8 7Z" /><path d="M8.5 12h.01M12 12h.01M15.5 12h.01" /></svg>;
  }
  if (category === 'forum') {
    return <svg {...common}><path d="M5 5.5h14v10H9l-4 3v-13Z" /><path d="M8.5 9h7M8.5 12h4.5" /></svg>;
  }
  return <svg {...common}><path d="M6 4.5h12v15H6z" /><path d="m9 12 2 2 4-4M9 7h6M9 17h6" /></svg>;
};

const HomeNotificationCenter = ({
  notifMenuOpen,
  unifiedUnreadCount,
  forumUnreadCount,
  forumNotifPreview,
  feedUnreadCount,
  feedNotifPreview,
  chatUnreadCount,
  notifAnchorRect,
  bellWrapRef,
  handleBellClick,
  closeNotifMenu,
  navigate,
  handleGoToFeed,
  markFeedNotificationsReadAll,
  markAllNotificationsRead,
}) => {
  const { t } = useTranslation('home');

  useEffect(() => {
    if (!notifMenuOpen) return undefined;
    const onKeyDown = (event) => {
      if (event?.key === 'Escape' || event?.key === 'Esc') {
        closeNotifMenu({ restoreFocus: true });
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [notifMenuOpen, closeNotifMenu]);

  const categories = useMemo(() => {
    const rows = [
      {
        id: 'messages',
        title: t('notifications.messages'),
        count: Number(chatUnreadCount || 0),
        summaryKey: 'notifications.messagesSummary',
        onOpen: () => navigate('/contacts'),
      },
      {
        id: 'forum',
        title: t('notifications.forum'),
        count: Number(forumUnreadCount || 0),
        summaryKey: 'notifications.forumSummary',
        preview: forumNotifPreview?.text || '',
        onOpen: () => navigate('/foro', { state: { openNotifs: true } }),
      },
      {
        id: 'feed',
        title: t('notifications.feed'),
        count: Number(feedUnreadCount || 0),
        summaryKey: 'notifications.feedSummary',
        preview: feedNotifPreview?.text || '',
        onOpen: async () => {
          try { await markFeedNotificationsReadAll(); } catch {}
          handleGoToFeed();
        },
      },
    ];

    return rows.sort((a, b) => {
      const unreadFirst = Number(b.count > 0) - Number(a.count > 0);
      return unreadFirst || 0;
    });
  }, [chatUnreadCount, forumUnreadCount, forumNotifPreview, feedUnreadCount, feedNotifPreview, t, navigate, handleGoToFeed, markFeedNotificationsReadAll]);

  const viewportWidth = typeof window === 'undefined' ? 390 : window.innerWidth;
  const viewportHeight = typeof window === 'undefined' ? 844 : window.innerHeight;
  const horizontalMargin = 12;
  const panelWidth = Math.min(380, Math.max(0, viewportWidth - horizontalMargin * 2));
  const maxPanelHeight = Math.min(520, Math.max(220, viewportHeight - 24));
  const desiredLeft = notifAnchorRect
    ? notifAnchorRect.right - panelWidth
    : viewportWidth - horizontalMargin - panelWidth;
  const panelLeft = clamp(desiredLeft, horizontalMargin, Math.max(horizontalMargin, viewportWidth - panelWidth - horizontalMargin));
  const desiredTop = notifAnchorRect ? notifAnchorRect.bottom + 8 : 64;
  const panelTop = clamp(desiredTop, 12, Math.max(12, viewportHeight - maxPanelHeight - 12));
  const hasUnread = unifiedUnreadCount > 0;

  const openCategory = async (row) => {
    closeNotifMenu();
    await row.onOpen();
  };

  return (
    <>
      <button
        type="button"
        onClick={handleBellClick}
        ref={bellWrapRef}
        aria-label={t('notifications.unread', { count: unifiedUnreadCount })}
        aria-expanded={notifMenuOpen}
        aria-controls="home-notification-panel"
        aria-haspopup="dialog"
        style={{
          width: 44,
          height: 44,
          minWidth: 44,
          flex: '0 0 44px',
          borderRadius: '50%',
          border: '1px solid rgba(218,165,32,.55)',
          background: notifMenuOpen
            ? 'radial-gradient(circle at 34% 22%, rgba(255,255,255,.22), transparent 34%), rgba(44,0,88,.88)'
            : 'radial-gradient(circle at 34% 22%, rgba(255,255,255,.18), transparent 34%), rgba(10,0,26,.64)',
          color: '#FFD700',
          fontSize: 18,
          cursor: 'pointer',
          position: 'relative',
          display: 'grid',
          placeItems: 'center',
          lineHeight: 1,
          padding: 0,
          boxSizing: 'border-box',
          aspectRatio: '1 / 1',
          touchAction: 'manipulation',
          transition: 'transform 140ms ease, background 140ms ease, box-shadow 140ms ease',
          boxShadow: notifMenuOpen ? '0 8px 22px rgba(0,0,0,.30)' : 'none',
        }}
      >
        <span aria-hidden="true">🔔</span>
        {hasUnread ? (
          <span
            aria-hidden="true"
            style={{
              position: 'absolute',
              right: -3,
              top: -4,
              minWidth: 18,
              height: 18,
              padding: '0 3px',
              borderRadius: 999,
              background: '#8A2BE2',
              border: '1px solid rgba(255,215,0,.65)',
              color: '#FFD700',
              fontSize: 10,
              fontWeight: 950,
              display: 'grid',
              placeItems: 'center',
              boxSizing: 'border-box',
            }}
          >
            {unifiedUnreadCount > 9 ? '9+' : unifiedUnreadCount}
          </span>
        ) : null}
      </button>

      {notifMenuOpen ? (
        <div
          onClick={(event) => {
            if (event.target === event.currentTarget) closeNotifMenu();
          }}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 99990,
            background: 'rgba(0,0,0,.18)',
            backdropFilter: 'blur(2px)',
            WebkitBackdropFilter: 'blur(2px)',
          }}
        >
          <section
            id="home-notification-panel"
            role="dialog"
            aria-modal="true"
            aria-labelledby="home-notification-title"
            onClick={(event) => event.stopPropagation()}
            style={{
              position: 'fixed',
              left: panelLeft,
              top: panelTop,
              width: panelWidth,
              maxWidth: 'calc(100vw - 24px)',
              maxHeight: maxPanelHeight,
              overflowY: 'auto',
              overscrollBehavior: 'contain',
              WebkitOverflowScrolling: 'touch',
              borderRadius: 18,
              padding: 12,
              color: '#FFD700',
              background: 'linear-gradient(145deg, rgba(75,0,130,.98), rgba(24,0,52,.98))',
              border: '1px solid rgba(218,165,32,.30)',
              boxShadow: '0 24px 58px rgba(0,0,0,.58), inset 0 1px 0 rgba(255,255,255,.08)',
              backdropFilter: 'blur(14px)',
              WebkitBackdropFilter: 'blur(14px)',
              boxSizing: 'border-box',
              animation: 'homeNotificationPanelIn 140ms ease-out',
            }}
          >
            <style>{'@keyframes homeNotificationPanelIn { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: translateY(0); } } @keyframes homeNotificationEmptyIn { from { opacity: 0; } to { opacity: 1; } } .home-notification-row, .home-notification-chevron { transition: background 150ms ease, color 150ms ease; } .home-notification-row:hover, .home-notification-row:active { background: rgba(255,215,0,.04) !important; } .home-notification-row:hover .home-notification-chevron, .home-notification-row:active .home-notification-chevron { color: rgba(255,215,0,.68) !important; } @media (prefers-reduced-motion: reduce) { #home-notification-panel, [data-home-notification-empty] { animation: none !important; } .home-notification-row, .home-notification-chevron { transition: none !important; } }'}</style>
            <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: hasUnread ? 4 : 4 }}>
              <div style={{ minWidth: 0 }}>
                <strong id="home-notification-title" style={{ display: 'block', fontSize: 15, letterSpacing: '.01em' }}>
                  {t('notifications.title')}
                </strong>
                {hasUnread ? (
                  <span style={{ display: 'block', marginTop: 1, color: 'rgba(255,215,0,.68)', fontSize: 12, fontWeight: 700 }}>
                    {t('notifications.new', { count: unifiedUnreadCount })}
                  </span>
                ) : null}
              </div>
              <button
                type="button"
                onClick={() => closeNotifMenu({ restoreFocus: true })}
                aria-label={t('header.closeNotifications')}
                style={{
                  width: 44,
                  height: 44,
                  minWidth: 44,
                  borderRadius: 12,
                  border: '1px solid rgba(255,255,255,.12)',
                  background: 'rgba(255,255,255,.07)',
                  color: '#FFD700',
                  display: 'grid',
                  placeItems: 'center',
                  padding: 0,
                  lineHeight: 1,
                  cursor: 'pointer',
                  fontWeight: 900,
                }}
              >
                <span aria-hidden="true">✕</span>
              </button>
            </header>

            {hasUnread ? (
              <div style={{ display: 'grid' }}>
                <button
                  type="button"
                  onClick={() => markAllNotificationsRead?.()}
                  style={{
                    minHeight: 44,
                    border: 0,
                    background: 'transparent',
                    color: 'rgba(255,215,0,.76)',
                    cursor: 'pointer',
                    fontSize: 12,
                    fontWeight: 700,
                    justifySelf: 'start',
                    padding: '0 2px 1px',
                  }}
                >
                  {t('notifications.markAllRead')}
                </button>
                {categories.map((row, index) => {
                  const hasPreview = row.count > 0 && row.preview;
                  const isActive = row.count > 0;
                  const showBadge = isActive && row.id !== 'messages';
                  return (
                    <button
                      key={row.id}
                      type="button"
                      className="home-notification-row"
                      data-notification-category={row.id}
                      data-active={isActive ? 'true' : 'false'}
                      onClick={() => openCategory(row)}
                      aria-label={t('notifications.openCategory', { category: row.title })}
                      style={{
                        width: '100%',
                        minHeight: isActive ? 60 : 58,
                        border: 0,
                        borderTop: index ? '1px solid rgba(255,255,255,.055)' : 0,
                        borderLeft: isActive ? '2px solid rgba(255,215,0,.78)' : '2px solid transparent',
                        borderRadius: 0,
                        padding: isActive ? '9px 5px 9px 8px' : '8px 5px 8px 8px',
                        background: isActive ? 'rgba(255,215,0,.025)' : 'transparent',
                        color: '#FFD700',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        textAlign: 'left',
                        cursor: 'pointer',
                        boxSizing: 'border-box',
                      }}
                    >
                      <span aria-hidden="true" data-testid={`notification-category-icon-${row.id}`} style={{ flex: '0 0 22px', width: 22, color: isActive ? '#FFD700' : 'rgba(255,215,0,.55)', display: 'grid', placeItems: 'center' }}><CategoryIcon category={row.id} /></span>
                      <span style={{ minWidth: 0, flex: '1 1 auto' }}>
                        <strong style={{ display: 'block', fontSize: 13, color: isActive ? '#FFD700' : 'rgba(255,255,255,.60)' }}>{row.title}</strong>
                        {hasPreview ? (
                          <span
                            data-testid="home-notification-feed-preview"
                            data-clamp-lines="2"
                            style={{
                              display: '-webkit-box',
                              marginTop: 3,
                              overflow: 'hidden',
                              color: 'rgba(255,255,255,.76)',
                              fontSize: 12,
                              fontWeight: 650,
                              lineHeight: 1.3,
                              WebkitBoxOrient: 'vertical',
                              WebkitLineClamp: 2,
                            }}
                          >
                            {row.preview}
                          </span>
                        ) : (
                          <span style={{ display: 'block', marginTop: 3, color: isActive ? 'rgba(255,255,255,.76)' : 'rgba(255,255,255,.60)', fontSize: 12, fontWeight: isActive ? 650 : 500 }}>
                            {isActive ? t(row.summaryKey, { count: row.count }) : t('notifications.noNewActivity')}
                          </span>
                        )}
                      </span>
                      {showBadge ? (
                        <span data-testid={`notification-unread-badge-${row.id}`} style={{ minWidth: 18, height: 18, padding: '0 4px', borderRadius: 999, background: 'rgba(138,43,226,.82)', border: '1px solid rgba(255,215,0,.55)', color: '#FFD700', display: 'grid', placeItems: 'center', fontSize: 9, fontWeight: 900, boxSizing: 'border-box' }}>
                          {displayCount(row.count)}
                        </span>
                      ) : null}
                      <span aria-hidden="true" className="home-notification-chevron" style={{ color: isActive ? 'rgba(255,215,0,.45)' : 'rgba(255,255,255,.45)', fontSize: 18, lineHeight: 1 }}>›</span>
                    </button>
                  );
                })}
              </div>
            ) : (
              <div data-home-notification-empty style={{ minHeight: 62, padding: '5px 10px', display: 'grid', placeItems: 'center', textAlign: 'center', boxSizing: 'border-box', animation: 'homeNotificationEmptyIn 200ms ease-out' }}>
                <div style={{ maxWidth: 190 }}>
                  <span aria-hidden="true" style={{ display: 'block', height: 30, marginBottom: 8, color: 'rgba(255,215,0,.60)' }}>
                    <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.35" strokeLinecap="round" strokeLinejoin="round" focusable="false">
                      <path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9" />
                      <path d="M10 21h4" />
                    </svg>
                  </span>
                  <strong style={{ display: 'block', color: '#FFD700', fontSize: 15, fontWeight: 700, lineHeight: 1.25, letterSpacing: '.01em' }}>{t('notifications.emptyTitle')}</strong>
                  <span style={{ display: 'block', marginTop: 10, color: 'rgba(255,255,255,.70)', fontSize: 12, fontWeight: 500, lineHeight: 1.4 }}>{t('notifications.emptyBody')}</span>
                </div>
              </div>
            )}
          </section>
        </div>
      ) : null}
    </>
  );
};

export default HomeNotificationCenter;
